import { AnimatePresence, motion } from 'framer-motion';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import Popper, { PopperContainer, PopperContainerProps, PopperProps } from '../../Popper';
import { Portal } from '../../Portal';
import { ClickAnchorContextProvider } from '../context';

export interface ClickAnchorProps
  extends Pick<PopperProps, 'className' | 'shouldHide' | 'floatingElement' | 'ignoreHideTrigger'>,
    Omit<PopperContainerProps, 'childrenEl' | 'children'> {
  disabled?: boolean;
  onOpen?(): void;
  onClose?(): void;
  inPortal?: boolean;
  clickMode?: 'open' | 'toggle';
  defaultOpen?: boolean;
  referenceElementDataTestId?: string;
  children?:
    | ((c: {
        open: boolean;
        forceFloatingElementUpdate(): void;
        setOpen: Dispatch<SetStateAction<boolean>>;
      }) => JSX.Element)
    | JSX.Element
    | React.ReactNode;
}

/**
 * @deprecated Use click anchor from packages/elements/src/UI/components/ClickAnchorNew.tsx (@elements-financial/elements/UI/components/ClickAnchor)
 */
export const ClickAnchor: FC<ClickAnchorProps> = ({
  onOpen,
  offset,
  onClose,
  children,
  disabled,
  placement,
  className,
  shouldHide,
  inPortal = true,
  floatingElement,
  ignoreHideTrigger,
  referenceElementDataTestId,
  popperJSHideModifierOptions,
  popperJSPreventOverflowOptions,
  popperJSFlipModifierOptions,
  clickMode = 'open',
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [childrenEl, setChildrenEl] = useState<HTMLDivElement | null>(null);

  function openMenu() {
    // Delays the opening of the menu as to not trigger the Popper's hide listener
    const timer = setTimeout(() => {
      setOpen(true);
    }, 100);
    return () => clearTimeout(timer);
  }

  function handleClick<E extends { preventDefault: () => void; stopPropagation: () => void }>(e: E) {
    if (clickMode === 'open') openMenu();
    else {
      e.preventDefault();
      e.stopPropagation();
      setOpen((p) => !p);
    }
  }

  useEffect(() => {
    if (open) onOpen?.();
    else onClose?.();
  }, [open]);

  return (
    <>
      {disabled ? (
        typeof children === 'function' ? (
          children({
            open,
            setOpen,
            forceFloatingElementUpdate: () => {},
          })
        ) : (
          children
        )
      ) : (
        <PopperContainer
          offset={offset}
          placement={placement}
          childrenEl={childrenEl}
          popperJSFlipModifierOptions={popperJSFlipModifierOptions}
          popperJSHideModifierOptions={popperJSHideModifierOptions}
          popperJSPreventOverflowOptions={popperJSPreventOverflowOptions}
        >
          {({ setPopperEl, popperStyles, popperAttributes, forceFloatingElementUpdate }) => (
            <>
              <motion.div
                role="button"
                onTap={handleClick}
                onClick={handleClick}
                onKeyDown={handleClick}
                onTapStart={handleClick}
                ref={(ref) => setChildrenEl(ref)}
                data-testid={referenceElementDataTestId}
                className="click-anchor-reference-element-trigger cursor-pointer"
              >
                {typeof children === 'function'
                  ? children({
                      open,
                      forceFloatingElementUpdate: forceFloatingElementUpdate ?? (() => {}),
                      setOpen,
                    })
                  : children}
              </motion.div>
              {((content: JSX.Element) => (inPortal ? <Portal>{content}</Portal> : content))(
                <ClickAnchorContextProvider
                  open={open}
                  setOpen={setOpen}
                  forceFloatingElementUpdate={forceFloatingElementUpdate}
                >
                  <AnimatePresence onExitComplete={onClose}>
                    {open && (
                      <Popper
                        setOpen={setOpen}
                        className={className}
                        shouldHide={shouldHide}
                        setPopperEl={setPopperEl}
                        popperStyles={popperStyles}
                        floatingElement={floatingElement}
                        popperAttributes={popperAttributes}
                        ignoreHideTrigger={ignoreHideTrigger}
                        forceFloatingElementUpdate={forceFloatingElementUpdate}
                      />
                    )}
                  </AnimatePresence>
                </ClickAnchorContextProvider>
              )}
            </>
          )}
        </PopperContainer>
      )}
    </>
  );
};
